import type { FormEventHandler, ReactNode } from 'react';
import {
  Button,
  CardContent,
  Input,
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@design-system';
import { ArrowLeft, Briefcase, Loader2, Phone } from 'lucide-react';
import type { SupportedCountryCode } from '@client/util/clerkAuthHelpers';
import { PhoneNumberField } from './PhoneNumberField';

interface SharedProps {
  eyebrow?: string;
  title: string;
  description: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
  submitLabel: string;
  isSubmitting: boolean;
  submitDisabled?: boolean;
  error?: ReactNode;
  beforeFields?: ReactNode;
  afterSubmit?: ReactNode;
  backAction?: { label: string; onClick: () => void };
}

interface PhoneStepProps extends SharedProps {
  step: 'phone';
  countryCode: SupportedCountryCode;
  phoneNumber: string;
  onCountryCodeChange: (value: SupportedCountryCode) => void;
  onPhoneNumberChange: (value: string) => void;
  phoneError?: ReactNode;
  autoFocus?: boolean;
}

interface VerifyStepProps extends SharedProps {
  step: 'verify';
  phoneNumber: string;
  phoneCode: string;
  onPhoneCodeChange: (value: string) => void;
  onResend: () => void;
  resendLabel?: string;
  resendDisabled?: boolean;
}

export type AdvisorPhoneVerificationFlowProps = PhoneStepProps | VerifyStepProps;

/**
 * The single presentation for advisor phone collection and verification. Normal advisor sign-up
 * and client-to-advisor activation deliberately provide different Clerk handlers, but share every
 * visible element through this component.
 */
export function AdvisorPhoneVerificationFlow(props: AdvisorPhoneVerificationFlowProps) {
  const isPhoneStep = props.step === 'phone';

  return (
    <>
      <AdvisorSignupCardHeader
        icon={
          isPhoneStep ? (
            <Briefcase className="text-primary-dark size-5" />
          ) : (
            <Phone className="text-primary-dark size-5" />
          )
        }
        eyebrow={props.eyebrow ?? (isPhoneStep ? 'Advisor Sign-Up' : 'Verify Your Phone')}
        title={props.title}
        description={props.description}
      />
      <CardContent className="p-6">
        <form
          onSubmit={props.onSubmit}
          autoComplete="off"
          className={isPhoneStep ? 'space-y-4' : 'space-y-6'}
        >
          {props.beforeFields}

          {props.step === 'phone' ? (
            <PhoneNumberField
              id="advisor-phone"
              name="advisor-phone-manual-entry"
              value={{
                countryCode: props.countryCode,
                phoneNumber: props.phoneNumber,
              }}
              onChange={{
                countryCode: props.onCountryCodeChange,
                phoneNumber: props.onPhoneNumberChange,
              }}
              validation={{ error: props.phoneError }}
              options={{
                disabled: props.isSubmitting,
                autoFocus: props.autoFocus,
              }}
            />
          ) : (
            <PhoneCodeField
              phoneNumber={props.phoneNumber}
              phoneCode={props.phoneCode}
              onPhoneCodeChange={props.onPhoneCodeChange}
              onResend={props.onResend}
              resendLabel={props.resendLabel}
              resendDisabled={props.resendDisabled}
              disabled={props.isSubmitting}
            />
          )}

          {props.error}

          <Button
            type="submit"
            className="w-full py-6"
            disabled={props.isSubmitting || props.submitDisabled}
          >
            {props.isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {props.submitLabel}
          </Button>

          {props.afterSubmit}

          {props.backAction && (
            <button
              type="button"
              className="flex w-full items-center justify-center gap-1 text-sm text-gray-600 transition-colors hover:text-gray-800"
              onClick={props.backAction.onClick}
            >
              <ArrowLeft className="h-4 w-4" />
              {props.backAction.label}
            </button>
          )}
        </form>
      </CardContent>
    </>
  );
}

export function AdvisorSignupCardHeader({
  icon,
  eyebrow,
  title,
  description,
}: {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <div className="p-1">
      <div className="bg-muted rounded-xl px-4 py-8 text-center">
        <div className="mb-2 flex items-center justify-center gap-2">
          {icon}
          <span className="text-primary-dark text-xs font-semibold uppercase tracking-widest">
            {eyebrow}
          </span>
        </div>
        <h1 className="text-xl font-bold text-gray-950">{title}</h1>
        <p className="text-foreground mx-auto mt-1 max-w-xs text-sm">{description}</p>
      </div>
    </div>
  );
}

export function PhoneCodeField({
  phoneNumber,
  phoneCode,
  onPhoneCodeChange,
  onResend,
  resendLabel = 'Resend text code',
  disabled,
  resendDisabled,
}: {
  phoneNumber: string;
  phoneCode: string;
  onPhoneCodeChange: (value: string) => void;
  onResend: () => void;
  resendLabel?: string;
  disabled: boolean;
  resendDisabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <Phone className="h-4 w-4 text-gray-400" />
        Phone code
      </label>
      <p className="text-xs text-gray-600">
        Sent to <span className="font-medium text-gray-800">{phoneNumber}</span>
      </p>
      <InputOTP
        maxLength={6}
        value={phoneCode}
        onChange={onPhoneCodeChange}
        disabled={disabled}
        autoFocus
        containerClassName="w-full gap-3"
      >
        <InputOTPGroup className="flex-1">
          {[0, 1, 2].map(index => (
            <InputOTPSlot
              key={index}
              index={index}
              className="h-12 flex-1 text-base"
            />
          ))}
        </InputOTPGroup>
        <InputOTPSeparator />
        <InputOTPGroup className="flex-1">
          {[3, 4, 5].map(index => (
            <InputOTPSlot
              key={index}
              index={index}
              className="h-12 flex-1 text-base"
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
      <button
        type="button"
        className="text-primary hover:text-primary-hover disabled:hover:text-primary text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
        onClick={onResend}
        disabled={resendDisabled || disabled}
      >
        {resendLabel}
      </button>
    </div>
  );
}
